/**
 * MA-ARCH-134: Message Optimization Recommendations
 * MA-ARCH-135: Enhanced with confidence levels and sample size guardrails
 * MA-ARCH-136A: UI Polish - Decision Clarity Upgrade
 * 
 * Displays actionable recommendations based on message variant performance:
 * - SCALE: High performers to expand (GREEN)
 * - FIX: Issues that need attention (AMBER)
 * - PAUSE: Low performers to reconsider (RED)
 * 
 * Rules-based, deterministic, grounded in current data.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { 
  TrendingUp, 
  Wrench, 
  PauseCircle, 
  Sparkles, 
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Users,
  Target,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  Info
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '@/lib/api';
import { WarningBanner, HELPER_DEFINITIONS } from './HelperText';

function useMessageOptimization({ period, campaignId, profitPathId, workspaceId, channel, vertical, tenantId }) {
  return useQuery({
    queryKey: ['message-optimization', period, campaignId, profitPathId, workspaceId, channel, vertical, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (profitPathId && profitPathId !== 'all') params.append('profit_path_id', profitPathId);
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (channel && channel !== 'all') params.append('channel', channel);
      if (vertical && vertical !== 'all') params.append('vertical', vertical);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const response = await api.get(`/analytics/message-optimization?${params}`);
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });
}

// MA-ARCH-135: Confidence level configuration
// MA-ARCH-136A: Enhanced with shield visual hierarchy (green/amber/red)
const confidenceConfig = {
  high: {
    icon: ShieldCheck,
    label: 'High',
    bgClass: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    iconClass: 'text-emerald-400',
    tooltip: HELPER_DEFINITIONS.confidence_badge + ' - Strong data volume',
  },
  medium: {
    icon: ShieldAlert,
    label: 'Medium',
    bgClass: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
    iconClass: 'text-amber-400',
    tooltip: HELPER_DEFINITIONS.confidence_badge + ' - Moderate data volume',
  },
  low: {
    icon: ShieldQuestion,
    label: 'Low',
    bgClass: 'bg-red-500/20 text-red-300 border border-red-500/30',
    iconClass: 'text-red-400',
    tooltip: HELPER_DEFINITIONS.confidence_badge + ' - Limited data, interpret with caution',
  },
};

// MA-ARCH-136A: Visual hierarchy - SCALE (green), FIX (amber), PAUSE (red)
const recommendationConfig = {
  scale: {
    icon: TrendingUp,
    label: 'SCALE',
    color: 'emerald',
    bgClass: 'bg-emerald-500/10 border-emerald-500/40',
    textClass: 'text-emerald-400',
    badgeClass: 'bg-emerald-500/30 text-emerald-300 border border-emerald-400/50',
    description: 'Increase traffic to this high performer',
    actionVerb: 'Scale up',
  },
  fix: {
    icon: Wrench,
    label: 'FIX',
    color: 'amber',
    bgClass: 'bg-amber-500/10 border-amber-500/40',
    textClass: 'text-amber-400',
    badgeClass: 'bg-amber-500/30 text-amber-300 border border-amber-400/50',
    description: 'Address issues to improve performance',
    actionVerb: 'Investigate',
  },
  pause: {
    icon: PauseCircle,
    label: 'PAUSE',
    color: 'red',
    bgClass: 'bg-red-500/10 border-red-500/40',
    textClass: 'text-red-400',
    badgeClass: 'bg-red-500/30 text-red-300 border border-red-400/50',
    description: 'Consider stopping or restructuring',
    actionVerb: 'Pause',
  },
};

function RecommendationCard({ recommendation, compact = false }) {
  const config = recommendationConfig[recommendation.type];
  const Icon = config.icon;
  
  // MA-ARCH-135: Get confidence config
  const confidence = recommendation.confidence || 'medium';
  const confConfig = confidenceConfig[confidence];
  const ConfidenceIcon = confConfig.icon;
  
  if (compact) {
    return (
      <div className={cn(
        "p-3 rounded-lg border",
        config.bgClass
      )} data-testid={`recommendation-card-${recommendation.type}`}>
        <div className="flex items-start gap-3">
          <div className={cn("p-1.5 rounded", config.badgeClass)}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={cn("text-xs font-bold uppercase tracking-wide", config.textClass)}>
                {config.label}
              </span>
              {/* MA-ARCH-136A: Message Variant prominently displayed */}
              <span className="text-sm font-semibold text-white truncate" data-testid="message-variant">
                {recommendation.message_variant}
              </span>
              {/* MA-ARCH-136A: Confidence badge with shield icon */}
              <span 
                className={cn("flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium", confConfig.bgClass)}
                title={confConfig.tooltip}
                data-testid={`confidence-badge-${confidence}`}
              >
                <ConfidenceIcon className={cn("h-3 w-3", confConfig.iconClass)} />
                {confConfig.label}
              </span>
            </div>
            {/* MA-ARCH-136A: Decision context - why this recommendation */}
            <p className="text-xs text-slate-400 line-clamp-2">
              {recommendation.reason}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={cn(
      "p-4 rounded-lg border-2 transition-all hover:border-opacity-80",
      config.bgClass
    )} data-testid={`recommendation-card-${recommendation.type}`}>
      {/* MA-ARCH-136A: Clear visual header with action type */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={cn("p-2.5 rounded-lg", config.badgeClass)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Action type badge */}
              <span className={cn("text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded", config.badgeClass)}>
                {config.label}
              </span>
              {/* MA-ARCH-136A: Confidence badge with shield visual */}
              <span 
                className={cn("flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-medium", confConfig.bgClass)}
                title={confConfig.tooltip}
                data-testid={`confidence-badge-${confidence}`}
              >
                <ConfidenceIcon className={cn("h-3 w-3", confConfig.iconClass)} />
                {confConfig.label} confidence
              </span>
            </div>
            {/* MA-ARCH-136A: Message Variant as the main title */}
            <h4 className="text-lg font-semibold text-white mt-1" data-testid="message-variant">
              {recommendation.message_variant}
            </h4>
          </div>
        </div>
        {recommendation.profit_path_name && (
          <span className="text-xs text-slate-400 bg-slate-800/50 px-2 py-1 rounded">
            {recommendation.profit_path_name}
          </span>
        )}
      </div>
      
      {/* MA-ARCH-136A: Decision context - WHY this recommendation exists */}
      <div className="mb-3 p-2.5 bg-slate-800/40 rounded border border-slate-700/50">
        <p className="text-sm text-slate-300">
          <span className={cn("font-medium", config.textClass)}>{config.actionVerb}:</span>{' '}
          {recommendation.reason}
        </p>
      </div>
      
      {/* MA-ARCH-136A: Key metrics with clear labels */}
      <div className="flex flex-wrap gap-2 text-xs">
        {recommendation.supporting_metrics.revenue_per_lead !== undefined && (
          <div className="flex items-center gap-1.5 bg-slate-800/60 px-2.5 py-1.5 rounded border border-slate-700/50">
            <DollarSign className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-slate-400">Rev/Lead:</span>
            <span className="text-white font-semibold">
              ${recommendation.supporting_metrics.revenue_per_lead.toLocaleString()}
            </span>
          </div>
        )}
        {recommendation.supporting_metrics.leads !== undefined && (
          <div className="flex items-center gap-1.5 bg-slate-800/60 px-2.5 py-1.5 rounded border border-slate-700/50">
            <Users className="h-3.5 w-3.5 text-blue-400" />
            <span className="text-slate-400">Leads:</span>
            <span className="text-white font-semibold">
              {recommendation.supporting_metrics.leads}
            </span>
          </div>
        )}
        {recommendation.supporting_metrics.alignment_rate !== undefined && (
          <div className="flex items-center gap-1.5 bg-slate-800/60 px-2.5 py-1.5 rounded border border-slate-700/50">
            <Target className="h-3.5 w-3.5 text-violet-400" />
            <span className="text-slate-400">Alignment:</span>
            <span className="text-white font-semibold">
              {recommendation.supporting_metrics.alignment_rate}%
            </span>
          </div>
        )}
        {recommendation.supporting_metrics.show_rate !== undefined && (
          <div className="flex items-center gap-1.5 bg-slate-800/60 px-2.5 py-1.5 rounded border border-slate-700/50">
            <CheckCircle2 className="h-3.5 w-3.5 text-cyan-400" />
            <span className="text-slate-400">Show Rate:</span>
            <span className="text-white font-semibold">
              {recommendation.supporting_metrics.show_rate}%
            </span>
          </div>
        )}
        {recommendation.supporting_metrics.percentile && (
          <div className={cn("px-2.5 py-1.5 rounded font-medium", config.badgeClass)}>
            {recommendation.supporting_metrics.percentile}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OptimizationRecommendations({
  period = '30d',
  campaignId,
  profitPathId,
  workspaceId,
  channel,
  vertical,
  tenantId,
  compact = false,
  maxRecommendations = 3,
}) {
  const { data, isLoading, error } = useMessageOptimization({
    period,
    campaignId,
    profitPathId,
    workspaceId,
    channel,
    vertical,
    tenantId,
  });

  if (isLoading) {
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-400" />
            Optimization Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-20 bg-slate-800 rounded" />
            <div className="h-20 bg-slate-800 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-400" />
            Optimization Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400">Unable to load recommendations</p>
        </CardContent>
      </Card>
    );
  }

  const { recommendations, summary } = data;
  const displayRecommendations = recommendations.slice(0, maxRecommendations);
  const hasRecommendations = recommendations.length > 0;

  if (compact) {
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-400" />
              <span className="font-medium text-white">Optimization Insights</span>
            </div>
            {hasRecommendations && (
              <div className="flex items-center gap-1.5 text-xs">
                {summary.recommendations_count.scale > 0 && (
                  <span className="bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded">
                    {summary.recommendations_count.scale} scale
                  </span>
                )}
                {summary.recommendations_count.fix > 0 && (
                  <span className="bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">
                    {summary.recommendations_count.fix} fix
                  </span>
                )}
                {summary.recommendations_count.pause > 0 && (
                  <span className="bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded">
                    {summary.recommendations_count.pause} pause
                  </span>
                )}
              </div>
            )}
          </div>
          
          {!hasRecommendations ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-400/50 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No immediate actions needed</p>
              <p className="text-xs text-slate-500 mt-1">All variants performing within normal range</p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayRecommendations.map((rec, idx) => (
                <RecommendationCard key={idx} recommendation={rec} compact={true} />
              ))}
              {recommendations.length > maxRecommendations && (
                <button className="w-full text-xs text-violet-400 hover:text-violet-300 flex items-center justify-center gap-1 pt-2">
                  View {recommendations.length - maxRecommendations} more
                  <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900/50 border-slate-700/50" data-testid="optimization-recommendations">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-400" />
              Optimization Recommendations
            </CardTitle>
            {/* MA-ARCH-136A: Helper text for decision clarity */}
            <p className="text-xs text-slate-500 mt-1">
              {HELPER_DEFINITIONS.optimization_recommendations}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400">{summary.total_variants} variants</span>
            <span className="text-slate-600">|</span>
            <span className="text-slate-400">${summary.total_revenue?.toLocaleString()} revenue</span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* MA-ARCH-136A: Low confidence warning banner */}
        {hasRecommendations && recommendations.some(r => r.confidence === 'low') && (
          <WarningBanner
            severity="info"
            icon={Info}
            message="Some recommendations are based on limited data. Consider waiting for more volume before acting."
            compact
          />
        )}
        
        {/* Summary badges - MA-ARCH-136A: Enhanced visual hierarchy */}
        {hasRecommendations && (
          <div className="flex items-center gap-2 flex-wrap">
            {summary.recommendations_count.scale > 0 && (
              <div className="flex items-center gap-1.5 bg-emerald-500/10 border-2 border-emerald-500/40 text-emerald-400 px-2.5 py-1 rounded-full text-xs font-medium">
                <TrendingUp className="h-3 w-3" />
                {summary.recommendations_count.scale} to SCALE
              </div>
            )}
            {summary.recommendations_count.fix > 0 && (
              <div className="flex items-center gap-1.5 bg-amber-500/10 border-2 border-amber-500/40 text-amber-400 px-2.5 py-1 rounded-full text-xs font-medium">
                <Wrench className="h-3 w-3" />
                {summary.recommendations_count.fix} to FIX
              </div>
            )}
            {summary.recommendations_count.pause > 0 && (
              <div className="flex items-center gap-1.5 bg-red-500/10 border-2 border-red-500/40 text-red-400 px-2.5 py-1 rounded-full text-xs font-medium">
                <PauseCircle className="h-3 w-3" />
                {summary.recommendations_count.pause} to PAUSE
              </div>
            )}
          </div>
        )}
        
        {!hasRecommendations ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 text-emerald-400/30 mx-auto mb-3" />
            <p className="text-base text-slate-300 font-medium">All Clear</p>
            <p className="text-sm text-slate-400 mt-1">No immediate optimization actions needed</p>
            <p className="text-xs text-slate-500 mt-2">
              All message variants are performing within expected ranges.
              <br />
              Top performer: <span className="text-violet-400">{summary.top_variant || 'N/A'}</span>
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayRecommendations.map((rec, idx) => (
              <RecommendationCard key={idx} recommendation={rec} />
            ))}
            
            {recommendations.length > maxRecommendations && (
              <div className="text-center pt-2">
                <button className="text-sm text-violet-400 hover:text-violet-300 flex items-center justify-center gap-1 mx-auto">
                  View all {recommendations.length} recommendations
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Thresholds info */}
        {hasRecommendations && (
          <div className="text-xs text-slate-500 border-t border-slate-700/50 pt-3 mt-3">
            <span className="text-slate-400">Thresholds:</span>{' '}
            Top 25% RPL ≥ ${summary.thresholds?.p75_revenue_per_lead?.toLocaleString() || 0} | 
            Bottom 25% RPL ≤ ${summary.thresholds?.p25_revenue_per_lead?.toLocaleString() || 0} | 
            Avg show rate: {summary.thresholds?.avg_show_rate || 0}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}
